<template>
    <v-container fluid>
        <!-- Freitext -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('Suchen') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field
                    v-model="searchParams.query"
                    dense
                    outlined
                    hide-details="auto"
                    :placeholder="$t('freitext_search')"
                />
            </v-col>
        </v-row>

        <!-- GenreGroups, aka Rubriken -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('rubriken') }}</label>
            </v-col>
            <v-col cols="9">
                <genre-group-select
                    v-model="searchParams.genre_group_ids"
                    :page-id="2"
                    clearable
                    :placeholder="$t('choose_multi')"
                    hide-details="auto"
                    multiple
                />
            </v-col>
        </v-row>

        <!-- Tags -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('tags') }}</label>
            </v-col>
            <v-col cols="9">
                <tag-select
                    v-model="searchParams.tag_ids"
                    for-magazin
                    clearable
                    show-download
                    :placeholder="$t('choose_multi')"
                    hide-details="auto"
                />
            </v-col>
        </v-row>

        <!-- Dossier -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('dossier') }}</label>
            </v-col>
            <v-col cols="9">
                <dossier-select
                    v-model="searchParams.dossier_ids"
                    clearable
                    :placeholder="$t('choose_multi')"
                    hide-details="auto"
                    multiple
                />
            </v-col>
        </v-row>

        <!-- Autoren -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('autor') }}</label>
            </v-col>
            <v-col cols="9">
                <autor-select
                    v-model="searchParams.autor_ids"
                    clearable
                    :placeholder="$t('choose_multi')"
                    hide-details="auto"
                    :only-active="!autorIncludeInactive"
                    multiple
                />
                <v-checkbox v-model="autorIncludeInactive" dense :label="$t('show_inactive_authors')" />
            </v-col>
        </v-row>

        <!-- Erstellt -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('erstellt') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.created_date_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.created_date_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- bearbeitet -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('bearbeitet') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.modified_date_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.modified_date_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- publiziert -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('publiziert') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.published_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.published_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- Kachelgrösse -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('kachelgroesse') }}</label>
            </v-col>
            <v-col cols="9">
                <kachelgroesse-select v-model="searchParams.overview_imagesize" hide-details="auto" clearable />
            </v-col>
        </v-row>

        <!-- Top-Thema -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-2">
                <label class="tgk-paragraph-text">{{ $t('top_thema') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <v-checkbox v-model="searchParams.is_top_thema" class="mt-0" hide-details="auto" />
                <span class="tgk-paragraph-text mt-2">{{ $t('top_thema_hint') }}</span>
            </v-col>
        </v-row>

        <!-- Newsletter -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-2">
                <label class="tgk-paragraph-text">{{ $t('nl') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <v-checkbox v-model="searchParams.to_newsletter" class="mt-0" hide-details="auto" />
                <span class="tgk-paragraph-text mt-2">{{ $t('nl_hint_magazin') }}</span>
            </v-col>
        </v-row>

        <!-- has linked events -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('has_linked_events') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.has_linked_events || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.has_linked_events = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Video -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('videobeitrag') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.has_video || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.has_video = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Kommentiert -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('kommentiert') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.has_comments || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.has_comments = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Bildstrecke -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('bildstrecke') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.has_photo_gallery || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.has_photo_gallery = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- ID -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('id') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field v-model="searchParams.id" type="number" dense outlined hide-details="auto" />
            </v-col>
        </v-row>

        <!-- UUID -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('uuid') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field v-model="searchParams.uuid" dense outlined hide-details="auto" />
            </v-col>
        </v-row>

        <v-row dense>
            <v-col class="d-flex justify-end">
                <PlainBtn @click="onResetBtn">{{ $t('reset_search') }}</PlainBtn>
                <ActionBtn class="ml-1" @click="onSearchBtn">
                    <KpIcon icon-cls="searchmagnifier" color="white" left />
                    {{ $t('search') }}
                </ActionBtn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import AutorSelect from '@/components/address/autor_select.vue';
import DossierSelect from '@/components/dossier/select.vue';
import GenreGroupSelect from '@/components/genre_group/select.vue';
import KachelgroesseSelect from '@/components/magazin/kachelgroesse_select.vue';
import TagSelect from '@/components/tag/select.vue';

/**
 * Enhanced Search Component for Magazin search
 *
 * Emits:
 * - search(params): when the user click on "search"
 * - reset: when the user clicks on "reset"
 *
 * The initial search params can be set via initial-params property.
 */
export default {
    name: 'MagazinEnhancedSearch',

    components: {
        AutorSelect,
        DossierSelect,
        GenreGroupSelect,
        KachelgroesseSelect,
        TagSelect,
    },

    props: {
        // the search params, emitted as 'input' on search btn click or reset
        initialParams: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            searchParams: { ...this.initialParams },
            autorIncludeInactive: false,
        };
    },

    methods: {
        getSearchParams() {
            return { ...this.searchParams };
        },

        onSearchBtn() {
            this.emitChangedParams();
        },

        onResetBtn() {
            this.searchParams = {};
            this.$emit('reset', {});
        },

        emitChangedParams() {
            this.$emit('search', { ...this.searchParams });
        },
    },
};
</script>
